import {
  RefObject,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useResizeObserver from "@react-hook/resize-observer";
import { getUniqueId } from "./random";
import { GlobalContext, StateUpdate } from "./context";

export const useUniqueId = (): string => useMemo(() => getUniqueId(), []);

export const useBusyWatcher = (): [
  boolean,
  <Type>(p: Promise<Type>) => Promise<Type>,
] => {
  // One thing that's tricky here is that requests are often made back to back, which will
  // result in the busy state being set to true and then immediately set to false. To avoid
  // this, we immediately set busy state to true when we observe an outbound request, but we
  // debounce the busy state to false so that it only goes back to false after a short delay.

  const busyRef = useRef<boolean>(false);
  const [state, dispatch] = useContext(GlobalContext);
  const [isBusy, setIsBusy] = useState<boolean>(busyRef.current);

  useEffect(() => {
    busyRef.current = state.busy;
    if (state.busy && !isBusy) {
      setIsBusy(true);
    }
    if (!state.busy && isBusy) {
      setTimeout(() => {
        if (!busyRef.current) {
          setIsBusy(false);
        }
      }, 100);
    }
  }, [state.busy]);

  const promiseWrapper = async <Type>(
    promise: Promise<Type>,
  ): Promise<Type> => {
    dispatch(StateUpdate.INCR_BUSY_COUNTER);
    try {
      return await promise;
    } finally {
      dispatch(StateUpdate.DECR_BUSY_COUNTER);
    }
  };

  return [isBusy, promiseWrapper];
};

export const useSize = <T extends HTMLElement>(
  target: RefObject<T>,
): DOMRect | null => {
  const [size, setSize] = useState<DOMRect | null>(null);

  useLayoutEffect(() => {
    if (target && target.current) {
      setSize(target.current.getBoundingClientRect());
    }
  }, [target]);

  useResizeObserver(target, (entry) => setSize(entry.contentRect));
  return size;
};

/**
 * Hook for syncing state with URL query parameters.
 *
 * Reads initial value from URL on mount, and updates URL when state changes.
 * Uses window.history.replaceState to avoid adding to browser history.
 *
 * @param key - The query parameter key
 * @param defaultValue - Default value if not present in URL
 * @param options - Optional configuration
 * @returns [value, setValue] tuple like useState
 */
export function useQueryParam<T extends string | boolean | number>(
  key: string,
  defaultValue: T,
  options?: {
    /** If true, skip URL updates during initial render */
    skipInitialSync?: boolean;
  },
): [T, (value: T) => void] {
  const isInitialMount = useRef(true);
  const skipInitialSync = options?.skipInitialSync ?? true;

  // Parse initial value from URL
  const getInitialValue = (): T => {
    const urlParams = new URLSearchParams(window.location.search);
    const rawValue = urlParams.get(key);

    if (rawValue === null) {
      return defaultValue;
    }

    // Parse based on type of default value
    if (typeof defaultValue === "boolean") {
      return (rawValue === "true") as T;
    }
    if (typeof defaultValue === "number") {
      const parsed = parseInt(rawValue, 10);
      return (Number.isNaN(parsed) ? defaultValue : parsed) as T;
    }
    return rawValue as T;
  };

  const [value, setValue] = useState<T>(getInitialValue);

  // Sync to URL when value changes
  useEffect(() => {
    // Skip initial mount if configured
    if (skipInitialSync && isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);

    // Only include non-default values in URL
    const isDefaultValue =
      value === defaultValue ||
      (typeof defaultValue === "boolean" && value === false) ||
      (typeof defaultValue === "number" && value === 0) ||
      (typeof defaultValue === "string" && value === "");

    if (isDefaultValue) {
      urlParams.delete(key);
    } else {
      urlParams.set(key, String(value));
    }

    const newUrl = `${window.location.pathname}${urlParams.toString() ? `?${urlParams.toString()}` : ""}`;
    window.history.replaceState({}, "", newUrl);
  }, [key, value, defaultValue, skipInitialSync]);

  return [value, setValue];
}
